import { Link } from "react-router-dom";
import { iconFor } from "@/lib/contentIcons";
import Seo from "@/components/Seo";
import { ABOUT_SRCSET, srcSetFor } from "@/data/images";
import PageHero from "@/components/common/PageHero";
import HelpCTA from "@/components/common/HelpCTA";
import { Reveal } from "@/components/common/Reveal";
import { useSite, useSiteImages } from "@/context/SiteDataContext";

const CHAPTERS = [
  {
    no: "01",
    title: "Every laboratory is unique",
    body: "At Athena Scientific, every challenge, every application, and every goal deserves a solution that is thoughtfully designed — not simply supplied. With more than a decade of experience in the scientific equipment industry, we support customers who want to improve workflow efficiency, analytical performance, operating costs or research capabilities.",
  },
  {
    no: "02",
    title: "We listen before we recommend",
    body: "Our approach begins with one question — How Can We Help You? We take time to understand your objectives, technical requirements and operational challenges before proposing any solution.",
  },
  {
    no: "03",
    title: "Wisdom over complexity",
    body: "Inspired by Athena, the symbol of wisdom and knowledge, we believe the right solution is not always the most complex one — but the one that best meets your needs today while supporting tomorrow's goals.",
  },
  {
    no: "04",
    title: "We build partnerships, not transactions",
    body: "Working closely with leading global manufacturers, we support customers with technical consultation, installation, application support, preventive maintenance, after-sales service and flexible equipment rental. We don't simply distribute scientific equipment — we build partnerships that empower laboratories to innovate, discover and make a meaningful impact.",
  },
];

const VALUES = [
  { icon: "Handshake", title: "Integrity", text: "We build trust through honesty, transparency, and accountability." },
  { icon: "Award", title: "Excellence", text: "We pursue the highest standards in everything we do." },
  { icon: "Lightbulb", title: "Innovation", text: "We embrace technologies that improve scientific outcomes." },
  { icon: "Brain", title: "Wisdom", text: "We combine knowledge, experience, and sound judgement to recommend the right solution for every laboratory." },
  { icon: "Users", title: "Partnership", text: "We believe lasting success comes from working together." },
  { icon: "HeartHandshake", title: "Customer Focus", text: "Every conversation starts with one question: How Can We Help You?" },
];

export default function About() {
  const SITE = useSite();
  const IMAGES = useSiteImages();
  return (
    <>
      <Seo routeKey="about" title="About Us" description="Athena Scientific is a scientific solutions partner with more than a decade of experience, focused on understanding laboratories before recommending the right technology." crumbs canonical={`https://${SITE.website}/about`} />
      <PageHero eyebrow="About Us" title="Driven by Science. Focused on You." description="A scientific solutions partner that listens before recommending — building long-term partnerships based on trust, expertise and customer success." crumbs={[{ label: "About Us" }]} />

      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <Reveal>
              <div className="overflow-hidden rounded-lg aspect-[4/5]">
                <img src={IMAGES.about} srcSet={srcSetFor(IMAGES.about, ABOUT_SRCSET)} sizes="(min-width: 1024px) 50vw, 100vw" width={1200} height={848} fetchPriority="high" decoding="async" alt="Scientists collaborating in a laboratory" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.05}>
                <div className="py-8 border-b border-border first:pt-0">
                  <div className="flex items-baseline gap-4">
                    <span className="text-aqua font-bold text-lg tabular-nums">{c.no}</span>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-ink">{c.title}</h2>
                  </div>
                  <p className="mt-4 text-greyish leading-relaxed md:pl-10">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-white noise">
        <div className="container-x py-20 md:py-28 grid md:grid-cols-2 gap-12">
          <Reveal>
            <p className="eyebrow text-white/60 before:bg-aqua">Our Mission</p>
            <p className="mt-6 text-2xl md:text-3xl font-semibold leading-snug text-balance">
              To empower scientific innovation by delivering reliable laboratory technologies, trusted expertise, and customer-focused solutions that help laboratories overcome challenges and achieve excellence.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow text-white/60 before:bg-aqua">Our Vision</p>
            <p className="mt-6 text-2xl md:text-3xl font-semibold leading-snug text-balance">
              To be Asia's trusted partner for scientific solutions, recognised for technical expertise, meaningful partnerships, and an unwavering commitment to advancing science.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <Reveal>
          <p className="eyebrow">Core Values</p>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink max-w-2xl text-balance">The principles behind every recommendation</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUES.map((v, i) => {
            const Icon = iconFor(v.icon);
            return (
              <Reveal key={v.title} delay={(i % 3) * 0.07}>
                <div className="h-full p-7 rounded-lg border border-border bg-white hover:border-aqua/40 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all">
                  <div className="w-12 h-12 rounded-md bg-aqua/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-aqua" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-ink">{v.title}</h3>
                  <p className="mt-2 text-greyish text-sm">{v.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <HelpCTA />
    </>
  );
}
